import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

const EXTERNAL_HANDLE_PREFIX = "ext_";
const EXTERNAL_HANDLE_PREFIX_WITH_AT = `@${EXTERNAL_HANDLE_PREFIX}`;

export function stripExternalHandlePrefix(value: string, isExternalCampaign: boolean): string {
  if (!isExternalCampaign || !value) {
    return value;
  }

  if (value.startsWith(EXTERNAL_HANDLE_PREFIX_WITH_AT)) {
    const stripped = value.slice(EXTERNAL_HANDLE_PREFIX_WITH_AT.length);
    return stripped ? `@${stripped}` : value;
  }

  if (value.startsWith(EXTERNAL_HANDLE_PREFIX)) {
    const stripped = value.slice(EXTERNAL_HANDLE_PREFIX.length);
    return stripped || value;
  }

  return value;
}

export function truncateText(value: string, maxLength: number, suffix = "..."): string {
  if (maxLength <= 0) return "";
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength)}${suffix}`;
}

const COMBINING_DIACRITICS = /[̀-ͯ]/g;
export const SLUG_MAX_LENGTH = 100;

export function toKebabSlug(value: string): string {
  return value
    .normalize("NFKD")
    .replace(COMBINING_DIACRITICS, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX_LENGTH);
}

const SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * The API's `slug()` rule (`apps/api/src/utils/zod.ts`): 2–100 characters, lowercase letters and
 * digits in runs joined by single hyphens. The API trims first; this checks the value as given.
 */
export const isValidSlug = (slug: string): boolean =>
  slug.length >= 2 && slug.length <= SLUG_MAX_LENGTH && SLUG_PATTERN.test(slug);

export const isFiniteNumber = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value);

/**
 * Display form of a social handle: exactly one leading `@`.
 *
 * Idempotent, so it is safe on values that may or may not already carry the
 * prefix — which is why it exists: the same one-line guard was written eight
 * times across the admin surfaces, in two spellings.
 *
 * Display only. The slug builders that feed `usePostPlatform`'s channel URLs
 * strip the prefix instead and are a separate concern.
 */
export function formatHandle(handle: string): string {
  return handle.startsWith("@") ? handle : `@${handle}`;
}
